/**
 * Teleport locomotion — which in a headset is not a comfort option, it is the
 * way this game moves.
 *
 * Two separate facts arrive at the same answer.
 *
 * **Comfort.** Smooth stick locomotion plus a hound in a corridor is how you
 * make somebody put a headset down for good. The person this was asked for
 * cannot test the game at all without teleport, and part of the household is
 * the same. So teleport is the default and smooth locomotion is what you opt
 * into, not the other way round.
 *
 * **Apple Vision Pro has no controllers.** Its WebXR input is
 * `transient-pointer`: a ray that exists *only at the instant of a pinch*, and
 * points where the player is looking. There is no thumbstick anywhere on the
 * device and no continuous gaze to poll, so smooth locomotion there is not
 * merely uncomfortable, it is inexpressible. Gaze-and-pinch teleport is the only
 * locomotion the hardware can state.
 *
 * ## Hold-to-dash
 *
 * The mode that was actually asked for, and the default. Hold, and the player
 * makes repeated short hops — 1.1 m crouched, 1.7 m walking, 2.3 m at a run —
 * each behind a quick fade, so ground goes by continuously with no optical flow
 * at all. On a controller the direction is the stick; on Vision Pro it is the
 * gaze ray, which exists only while pinching, which is exactly when a dash is
 * being held. The two fall out of the same code, which is the point of
 * `render/xrintent.ts`.
 *
 * `'arc'` is the classic point-and-release teleport, kept for longer jumps, and
 * `'smooth'` is the opt-in. See `XRMoveStyle`.
 *
 * ## The speed budget, which is the whole of the balance
 *
 * A hop is free ground. Left alone, a teleporter outruns a hound, wins the slide
 * surge's decision window without deciding anything, and makes no noise while
 * doing it. So every hop is *billed*: covering `d` metres costs `d / v` seconds
 * before the next hop may fire, where `v` is exactly the speed the flat game
 * would have moved this player at — `TUNING.player`'s crouch, walk and sprint
 * speeds, times the same water, backpedal and injury scales
 * `player/controller.ts` applies.
 *
 * Sustained speed is therefore `d / (d / v) = v`, identically, for every stance,
 * every hop length, and both teleport modes, which share one budget. A hop cut
 * short by a wall bills only the ground it actually covered, so grinding into a
 * corner is as slow in here as it is on a monitor.
 *
 * Noise is the other half of the same argument. `player.noise` is how the hound
 * finds you, and crouch-walking to stay quiet is the game's best counterplay; a
 * dash that made no noise would be silent running and would trivialise the one
 * entity that reliably kills. So the model below is a transcription of
 * `player/footsteps.ts` — same stance references, same effort curve, same water
 * bonus, same damping rate — fed with the ground the hops actually covered.
 *
 * ## What a hop is checked against
 *
 * Every hop is swept, not merely endpoint-tested: a standing test every
 * `SAMPLE_STEP` metres, each one a full body radius against the grid **and**
 * `propIndex`, with the hop landing on the last sample that passed. Consecutive
 * sample discs overlap, so nothing thinner than a wall can slip between two of
 * them — no blinking through walls, half-walls, play structures or pianos.
 * Scenery with no footprint is not in the index and is not an obstacle, so a
 * ball pit stays somewhere you can hop into.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp01, damp } from '../core/math';
import { Cell, type GeneratedLevel, type PlayerState } from '../core/types';
import type { XRIntent, XRIntentSet } from './xrintent';

/**
 * How the player moves in a session.
 *
 * `'dash'` is the default and the only one both headsets can express.
 * `render/xrlocomotion.ts` owns and persists the choice; the in-world comfort
 * screen edits it.
 */
export type XRMoveStyle = 'dash' | 'arc' | 'smooth';

/* ------------------------------------------------------------------ *
 * Numbers
 * ------------------------------------------------------------------ */

/**
 * Metres per hop, by stance.
 *
 * "A couple of metres" is what was asked for; walking and running sit inside
 * that and crouching is deliberately shorter, because a crouched hop the length
 * of a walking stride would cross ground faster than the crouch speed the
 * counterplay is priced against, and because short hops read as careful.
 *
 * Divided into the stance speed these give 1.14 hops/s crouched, 1.53 walking
 * and 2.17 running — a few a second at the end where it matters.
 */
const HOP_CROUCH = 1.1;
const HOP_WALK = 1.7;
const HOP_SPRINT = 2.3;

/** Below this a hop is not worth a fade, and is refused without being billed. */
const MIN_HOP = 0.12;

/**
 * Metres between standing tests along a hop.
 *
 * Half the body radius, which is the same number `player/collision.ts` uses for
 * its own substep and for the same reason — "below 1 there is no tunnelling".
 * A whole radius is *nearly* enough and is what this used to be: two clear
 * samples a radius apart can still straddle a box corner that the disc halfway
 * between them would touch, by up to 4 cm of geometry. Measured over four
 * thousand hops it showed up four times at up to 1.6 cm. Halving it costs about
 * ten box tests per hop and takes the worst case under a centimetre.
 */
const SAMPLE_STEP = TUNING.player.radius * 0.5;

/**
 * Seconds the primary action must be held before it means travel rather than a
 * press: before a ray-driven dash starts, before an arc appears, and before
 * `XRLocomotion.pinchTurn` reads a pinch as a turn.
 *
 * On a runtime with no buttons a short pinch is `interact` and a long one is
 * locomotion, so every consumer has to agree on where short ends or picking up a
 * granola bar would launch the player at it. Exported so there is exactly one
 * such number.
 */
export const HOLD_ARM_TIME = 0.28;

/**
 * Seconds of continuous dashing after which a stickless runtime breaks into a
 * run.
 *
 * The only way a Vision Pro can reach `sprintSpeed`, and it has to have one:
 * the slide surge is tuned around a player who can run, and a headset that could
 * only ever walk would lose that race before it started. Same lean-into-it idea
 * `TUNING.touch.sprintOn` uses for a thumb on glass. A controller ignores this
 * and uses the grip on the hand that is steering, which is the squeeze
 * `render/xrinput.ts` already calls sprint.
 */
const SPRINT_HOLD = 1.6;

/** Below this a stick is at rest as far as steering is concerned. */
const STICK_LIVE = 0.1;

/**
 * Which hand an intent came from.
 *
 * `XRIntent` does not declare handedness — it is deliberately the smallest shape
 * both headsets can fill, and a runtime is allowed to report none at all. The
 * producer's own `XRHandIntent` does carry it, and that is what actually turns
 * up here, so it is read off the object and validated rather than assumed.
 *
 * `'none'` is common and is not an error. It is treated as the move hand, which
 * is the right way round and matches `readSticks` in `render/xrinput.ts`: such a
 * source keeps walking, and loses only a turn it could not have placed on a
 * specific hand anyway.
 */
export function handOf(intent: XRIntent): 'left' | 'right' | 'none' {
  const h = (intent as Partial<{ handedness: unknown }>).handedness;
  return h === 'left' || h === 'right' ? h : 'none';
}

/**
 * Below this the ray is pointing at the player's own feet or at the ceiling,
 * and is taken to mean stop.
 *
 * It is the brake a stickless runtime otherwise has no way to express: look
 * down, and the dash you are holding ends without letting go of the pinch.
 */
const RAY_FLAT = 0.08;

/* -- The arc. -------------------------------------------------------- */

/** Launch speed and gravity for the drawn parabola, m/s and m/s². */
const ARC_SPEED = 8.0;
const ARC_GRAVITY = 9.8;

/** Metres of ground an arc may cross. It is billed like everything else. */
const ARC_MAX = 6.0;

/** Points in the drawn curve. Enough to read as a curve, few enough to be free. */
const ARC_POINTS = 24;

/* -- The fade. ------------------------------------------------------- */

/**
 * How dark a hop goes at a short one and at a long one, and how long the fade
 * runs before the hop commits.
 *
 * A hop is a cut rather than a movement, so this is not fighting optic flow —
 * it is stopping the picture from *snapping*, which is what would read as a
 * stutter. Short hops arrive twice a second, so theirs is a dip; an arc jump is
 * occasional and gets a proper blink.
 *
 * What these actually produce, once `render/xrvignette.ts` has damped the
 * blackout at its own 14/s, computed rather than guessed:
 *
 *     walk hop    75 ms out, target 0.70 -> peaks at 0.48 on screen,
 *                 visibly dim for 250 ms of a 654 ms interval  (38%)
 *     sprint hop  83 ms out, target 0.73 -> peaks at 0.50,
 *                 dim for 250 ms of a 460 ms interval          (54%)
 *     arc 6 m    127 ms out, target 1.00 -> peaks at 0.86,
 *                 dim for 347 ms of a 2.31 s interval          (15%)
 *
 * Two things follow that are worth saying out loud. The player never reaches
 * full black on a dash, because the damping cannot get there inside a pulse this
 * short — the hop commits at about half darkness, which for one to two metres of
 * cut is the intended trade and not an accident. And a held sprint spends over
 * half its time under some dimming, which is the number most likely to be wrong:
 * it may read as the rhythmic pulse it is meant to, or as a strobe. Nobody has
 * worn it. If it strobes, the fix is a shallower `FADE_NEAR_DEPTH` rather than a
 * shorter fade, because shortening it only lowers the peak further.
 */
const FADE_NEAR_DEPTH = 0.7;
const FADE_FAR_DEPTH = 1.0;
const FADE_NEAR_DIST = 2.0;
const FADE_FAR_DIST = 5.0;
const FADE_OUT_BASE = 0.055;
const FADE_OUT_PER_METRE = 0.012;

/**
 * How fast the published noise follows the movement, 1/s.
 *
 * The rate `player/footsteps.ts` damps at. It is what makes "stop moving" work
 * against a hound in about four tenths of a second, and a dashing player has to
 * go quiet on exactly the same schedule as a walking one.
 */
const NOISE_RATE = 11;

/* ------------------------------------------------------------------ *
 * Standing tests
 * ------------------------------------------------------------------ */

/**
 * True when a body of `radius` could stand at this point.
 *
 * The same predicate `player/collision.ts` resolves against, asked as a
 * question instead of as a push: a solid cell is a full box filling the cell, a
 * pillar cell is a small box in the middle of one, and solid props are exact
 * rotated boxes in `propIndex`. Answering it the same way is the point — a
 * teleport that landed somewhere the collision system disagreed with would put
 * the body inside geometry and leave the controller to shove it out, which is a
 * pop in both eyes at head speed.
 *
 * It used to be five point probes — centre, and the four points a radius away
 * on each axis — which is cheap and wrong in one specific way: point probes
 * cannot see a box *corner*. Measured over about four thousand hops across five
 * generated levels, that let a hop land with the body's disc buried up to 25 cm
 * into the corner of a wall or a pillar (33 landings, and 68 swept paths that
 * grazed one). None of them ever put the body's centre inside a wall, so
 * nothing was ever teleported through anything — but each one was a frame of
 * the body inside the scenery, and on a pillar floor they are common. A real
 * circle test costs four box tests instead of five array reads and removes the
 * whole class.
 *
 * `PropIndex.blocked` skips anything shorter than `stepOverHeight`, which is why
 * a ball pit — authored with no footprint at all — stays somewhere the player
 * can hop into, and a play structure does not.
 */
export function standable(level: GeneratedLevel, x: number, z: number, radius: number): boolean {
  const grid = level.grid;
  const cs = grid.cellSize;
  const lo = grid.worldToCell(x - radius, z - radius);
  const hi = grid.worldToCell(x + radius, z + radius);
  // A body is smaller than a cell, so this is at most a two-by-two.
  for (let cy = lo.cy; cy <= hi.cy; cy++) {
    for (let cx = lo.cx; cx <= hi.cx; cx++) {
      if (!grid.inBounds(cx, cy)) return false;
      const v = grid.at(cx, cy);
      if (v === Cell.Void || v === Cell.Wall) {
        if (circleHitsBox(x, z, radius, cx * cs, cy * cs, (cx + 1) * cs, (cy + 1) * cs)) return false;
      } else if (v === Cell.Pillar) {
        // The support in the middle of the cell. Same box the mesh and the
        // collision world both use; see `PILLAR_HALF_FRACTION` in
        // `player/collision.ts`.
        const half = cs * TUNING.player.pillarHalfFraction;
        const mx = (cx + 0.5) * cs;
        const mz = (cy + 0.5) * cs;
        if (circleHitsBox(x, z, radius, mx - half, mz - half, mx + half, mz + half)) return false;
      }
    }
  }
  return !level.propIndex.blocked(x, z, radius);
}

/** Circle against an axis-aligned box: nearest point, squared distance. */
function circleHitsBox(
  x: number,
  z: number,
  radius: number,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number
): boolean {
  const nx = x < minX ? minX : x > maxX ? maxX : x;
  const nz = z < minZ ? minZ : z > maxZ ? maxZ : z;
  const dx = x - nx;
  const dz = z - nz;
  return dx * dx + dz * dz < radius * radius;
}

/** Water underfoot at the landing, for the splash a hound can hear. */
function inWaterAt(level: GeneratedLevel, x: number, z: number): boolean {
  const grid = level.grid;
  const cell = grid.worldToCell(x, z);
  return grid.inBounds(cell.cx, cell.cy) && grid.at(cell.cx, cell.cy) === Cell.Water;
}

/**
 * How far a body may travel from (x, z) along a unit direction before something
 * stops it, up to `want` metres.
 *
 * Walks the line in `SAMPLE_STEP` increments and returns the last one that
 * passed. The starting point is deliberately *not* tested: a player whose body
 * has ended up inside the strict test's idea of geometry — wedged beside a prop,
 * or leaned into by their own head — has to be able to hop back out of it.
 */
function sweep(
  level: GeneratedLevel,
  x: number,
  z: number,
  dirX: number,
  dirZ: number,
  want: number,
  radius: number
): number {
  if (want < MIN_HOP) return 0;
  let reached = 0;
  for (let d = SAMPLE_STEP; ; d += SAMPLE_STEP) {
    const at = Math.min(d, want);
    if (!standable(level, x + dirX * at, z + dirZ * at, radius)) break;
    reached = at;
    if (at >= want) break;
  }
  return reached;
}

/** One planned hop: where it lands and how much ground it covers. */
interface Plan {
  x: number;
  z: number;
  distance: number;
}

/**
 * Plan a hop, sliding along geometry when the direct line is blocked.
 *
 * Walking into a wall at an angle slides along it in the flat game, and a
 * teleport that simply refused would make corridors unusable — the player would
 * be stopped dead by every doorframe they clipped. So a blocked direction is
 * retried on each axis alone, with the distance scaled by that axis's share of
 * the direction: a shallow angle gives most of a hop along the wall and a
 * square-on one gives nothing, which is what sliding is.
 */
function planHop(
  level: GeneratedLevel,
  fromX: number,
  fromZ: number,
  dirX: number,
  dirZ: number,
  want: number,
  radius: number,
  out: Plan
): Plan {
  let bx = dirX;
  let bz = dirZ;
  let best = sweep(level, fromX, fromZ, dirX, dirZ, want, radius);

  if (best < MIN_HOP) {
    const ax = Math.sign(dirX);
    const az = Math.sign(dirZ);
    if (ax !== 0) {
      const d = sweep(level, fromX, fromZ, ax, 0, want * Math.abs(dirX), radius);
      if (d > best) {
        best = d;
        bx = ax;
        bz = 0;
      }
    }
    if (az !== 0) {
      const d = sweep(level, fromX, fromZ, 0, az, want * Math.abs(dirZ), radius);
      if (d > best) {
        best = d;
        bx = 0;
        bz = az;
      }
    }
  }

  out.distance = best < MIN_HOP ? 0 : best;
  out.x = fromX + bx * out.distance;
  out.z = fromZ + bz * out.distance;
  return out;
}

/* ------------------------------------------------------------------ *
 * The arc's picture
 * ------------------------------------------------------------------ */

/**
 * The parabola and the landing ring.
 *
 * Built the first time an arc is aimed and never on a machine that has not
 * entered a session, for the reason `render/xr.ts` builds its vignette late: a
 * desktop that will never wear a headset should not be carrying the geometry.
 */
interface AimView {
  readonly object: THREE.Object3D;
  show(points: Float32Array, x: number, z: number, valid: boolean): void;
  hide(): void;
  dispose(): void;
}

/** Landing is legal, and landing is refused. */
const VALID_COLOR = 0x9fe8c0;
const INVALID_COLOR = 0xe8756a;

function makeAimView(): AimView {
  const group = new THREE.Group();
  group.name = 'xr-teleport-aim';

  const curveGeom = new THREE.BufferGeometry();
  const positions = new THREE.BufferAttribute(new Float32Array(ARC_POINTS * 3), 3);
  positions.setUsage(THREE.DynamicDrawUsage);
  curveGeom.setAttribute('position', positions);
  // The curve is rewritten every frame it is visible and never culled, so a
  // bounding sphere that covers the whole building costs one write instead of a
  // recompute per frame.
  curveGeom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

  // `fog: false` because the marker is an instrument rather than part of the
  // room: a landing spot that faded out at the far end of its own range would be
  // unreadable exactly where the player needs it.
  const curveMat = new THREE.LineBasicMaterial({
    color: VALID_COLOR,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    fog: false,
    toneMapped: false,
  });
  const curve = new THREE.Line(curveGeom, curveMat);
  curve.frustumCulled = false;
  curve.renderOrder = 900;

  const ringGeom = new THREE.RingGeometry(0.2, 0.3, 28);
  ringGeom.rotateX(-Math.PI / 2);
  const ringMat = new THREE.MeshBasicMaterial({
    color: VALID_COLOR,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: false,
    toneMapped: false,
  });
  const ring = new THREE.Mesh(ringGeom, ringMat);
  ring.frustumCulled = false;
  ring.renderOrder = 901;

  group.add(curve, ring);
  group.visible = false;

  return {
    object: group,

    show(points: Float32Array, x: number, z: number, valid: boolean): void {
      (positions.array as Float32Array).set(points);
      positions.needsUpdate = true;
      const color = valid ? VALID_COLOR : INVALID_COLOR;
      curveMat.color.setHex(color);
      ringMat.color.setHex(color);
      // A hair off the floor, or it z-fights the carpet.
      ring.position.set(x, 0.02, z);
      group.visible = true;
    },

    hide(): void {
      group.visible = false;
    },

    dispose(): void {
      group.parent?.remove(group);
      curveGeom.dispose();
      curveMat.dispose();
      ringGeom.dispose();
      ringMat.dispose();
    },
  };
}

/* ------------------------------------------------------------------ *
 * One frame of teleport
 * ------------------------------------------------------------------ */

/**
 * What one frame of teleport locomotion decided.
 *
 * Reused in place, so read it within the frame and never retain it.
 */
export interface XRHop {
  /** True on the frame a hop commits. */
  moved: boolean;
  /** Metres the body must move by, and the rig with it. Zero unless `moved`. */
  dx: number;
  dz: number;
  /** Ground this hop covered. Zero unless `moved`. What the noise is priced on. */
  distance: number;
  /** 0..1 blackout to fold into the session's shutter this frame. */
  fade: number;
  /** 0..1, the value `PlayerState.noise` should carry. */
  noise: number;
  /** Metres per second of ground the player is currently making. */
  speed: number;
  /**
   * True while the player is covering ground.
   *
   * The hide latch has to be told: `PlayerState.speed` is the *controller's*
   * travel and is zero throughout a teleport, so without this a crouched player
   * would stay `hidden` while dashing across the building, which is exactly the
   * exploit that would make the hound pointless.
   */
  travelling: boolean;
  /** True while a run-rate dash is running. */
  sprinting: boolean;
  /**
   * True while this style owns movement, so the caller must not also feed the
   * stick to `InputSink.axes` — that would move the body twice.
   */
  consumesStick: boolean;
}

/** Whichever hand is steering this frame, resolved onto the floor plane. */
interface Request {
  live: boolean;
  /** Unit direction on the floor plane, world space. */
  dirX: number;
  dirZ: number;
  /** True when it came from a ray rather than from a stick. */
  byRay: boolean;
  /** Metres to the floor point being looked at, or 0 for "as far as the hop goes". */
  reach: number;
  /** True when the request is behind the player, which the flat game makes slower. */
  backward: boolean;
  /** The steering hand's grip or second pinch, which is the run modifier. */
  secondary: boolean;
  /** The steering hand let go this frame. An arc commits on it. */
  ended: boolean;
  /** The steering ray, for the arc. */
  ray: { origin: THREE.Vector3; direction: THREE.Vector3 } | null;
}

/**
 * The teleport state machine: cadence, validation, the fade and the noise.
 *
 * Holds a frame of state and a lazily built aim view, so one instance lives for
 * the length of a session. It mutates nothing it is handed —
 * `render/xrlocomotion.ts` applies the result to the player and the rig.
 */
export class XRTeleport {
  /** Seconds of travel owed before the next hop may fire. The whole balance. */
  private cooldown = 0;

  /**
   * The fade running ahead of a hop that has been decided but not committed,
   * and the hop it is running ahead of.
   *
   * Held separately from the scratch `plan` because an arc keeps re-planning
   * every frame it is aimed, and a re-aim arriving during a charge would
   * otherwise rewrite the destination out from under it.
   */
  private charging = false;
  private chargeT = 0;
  private chargeFor = 0;
  private chargeDepth = 0;
  private pendX = 0;
  private pendZ = 0;
  private pendDistance = 0;
  private pendBackward = false;
  private pendWater = false;
  private pendCost = 0;

  /** Seconds since the last hop landed, and what that hop was worth. */
  private sinceHop = 0;
  private lastDistance = 0;
  private lastCost = 0;
  private lastWater = false;

  /** Published noise, damped exactly as `player/footsteps.ts` damps its own. */
  private noise = 0;

  /** Seconds the current travel request has been continuously live. */
  private holdTime = 0;
  private sprinting = false;

  /** The arc being aimed, in world space. */
  private aiming = false;
  private aimValid = false;
  private aimX = 0;
  private aimZ = 0;
  private aimDistance = 0;
  private readonly aimPoints = new Float32Array(ARC_POINTS * 3);
  private view: AimView | null = null;

  private readonly plan: Plan = { x: 0, z: 0, distance: 0 };
  private readonly req: Request = {
    live: false,
    dirX: 0,
    dirZ: 0,
    byRay: false,
    reach: 0,
    backward: false,
    secondary: false,
    ended: false,
    ray: null,
  };
  private readonly hop: XRHop = {
    moved: false,
    dx: 0,
    dz: 0,
    distance: 0,
    fade: 0,
    noise: 0,
    speed: 0,
    travelling: false,
    sprinting: false,
    consumesStick: false,
  };

  /**
   * The arc's parabola and landing ring, built on first use.
   *
   * Parent it to the **scene**, not to the rig: it marks a place in the
   * building, and a marker that travelled with the play space would slide out
   * from under the player on every snap turn. Safe to call on every session
   * start; it builds at most once.
   */
  aimView(): THREE.Object3D {
    this.view = this.view ?? makeAimView();
    return this.view.object;
  }

  /** Forget everything a session left behind. Called when one starts. */
  reset(): void {
    this.cooldown = 0;
    this.charging = false;
    this.chargeT = 0;
    this.chargeFor = 0;
    this.chargeDepth = 0;
    this.pendX = 0;
    this.pendZ = 0;
    this.pendDistance = 0;
    this.pendBackward = false;
    this.pendWater = false;
    this.pendCost = 0;
    this.sinceHop = 0;
    this.lastDistance = 0;
    this.lastCost = 0;
    this.lastWater = false;
    this.noise = 0;
    this.holdTime = 0;
    this.sprinting = false;
    this.cancelAim();
  }

  dispose(): void {
    this.view?.dispose();
    this.view = null;
  }

  /**
   * Decide this frame.
   *
   * `enabled` is the caller's "the simulation is running and nothing else owns
   * the pointer". Pass false while the player is frozen, while the game is
   * paused, and while an in-world panel is taking pinches — otherwise a player
   * clicking a menu button would also set off across the room.
   */
  update(
    set: XRIntentSet | null,
    player: PlayerState,
    level: GeneratedLevel,
    dt: number,
    style: XRMoveStyle,
    moveHand: 'left' | 'right',
    enabled: boolean
  ): XRHop {
    const out = this.hop;
    out.moved = false;
    out.dx = 0;
    out.dz = 0;
    out.distance = 0;
    out.fade = 0;
    out.consumesStick = style !== 'smooth';

    if (dt > 0) {
      // Floored at one frame below zero rather than at zero, so the remainder of
      // an interval that expired mid-frame carries into the next one instead of
      // being thrown away. Without the carry every hop waits for the first frame
      // *after* its interval and the whole feature runs about two per cent slow
      // — measured 2.550 m/s against a `walkSpeed` of 2.6, and 4.871 against a
      // `sprintSpeed` of 5.0. One frame is also all the credit a player standing
      // still may bank, so nobody can save up a burst.
      this.cooldown = Math.max(-dt, this.cooldown - dt);
      this.sinceHop += dt;
    }

    const usable = style !== 'smooth' && enabled && set !== null;
    // A hop that has not committed yet is abandoned the moment the simulation
    // stops. The alternative is a fade that started before a pause menu opened
    // landing the player somewhere a frame into it, or — much worse — a hop
    // committing into a level that `game.ts` is in the middle of replacing.
    // Letting go of the request does *not* cancel a charge, because there the
    // fade is the anticipation and the hop is owed.
    if (!usable) this.charging = false;

    const live = usable && set !== null ? this.resolve(set, player, style, moveHand) : false;

    if (live) {
      this.holdTime += dt;
      // A controller runs with the grip on the hand that is steering; a runtime
      // with no buttons at all leans into it instead.
      const wantsRun = this.req.secondary || (this.req.byRay && this.holdTime >= SPRINT_HOLD);
      this.sprinting = wantsRun && !player.crouching && !this.req.backward;
    } else {
      this.holdTime = 0;
      this.sprinting = false;
    }

    if (style === 'arc') this.updateArc(live, player, level, usable);
    else if (live && !this.charging && this.cooldown <= 0) this.planDash(player, level);

    if (this.charging) this.advanceCharge(dt, player, out);

    this.publish(dt, live && style === 'dash', player, out);
    return out;
  }

  /* ---------------------------------------------------------------- *
   * Steering
   * ---------------------------------------------------------------- */

  /**
   * Which hand is steering, and how. Fills `this.req`.
   *
   * A controller steers a dash with its stick and its ray is left for the arc.
   * That is not an oversight: a trigger held down is `interact` on the flat side
   * of the seam, and a Quest player pointing at a granola bar and pressing must
   * not set off across the room. A runtime with no stick has only the ray, and
   * only while pinching — which is when a dash is being held anyway.
   *
   * Only the move hand steers, on both paths, and that is load-bearing rather
   * than tidiness. Every hand's intent carries a full set of axes, so without
   * the filter a Quest player's *turn* stick would drive locomotion as well as
   * the rig; and on a stickless runtime the other hand's held pinch is the snap
   * turn (`XRLocomotion.pinchTurn`), which would otherwise dash and turn at once.
   */
  private resolve(
    set: XRIntentSet,
    player: PlayerState,
    style: XRMoveStyle,
    moveHand: 'left' | 'right'
  ): boolean {
    const req = this.req;
    req.live = false;
    req.ray = null;

    if (style === 'dash') {
      for (const intent of set.all) {
        if (intent.source !== 'controller') continue;
        const hand = handOf(intent);
        if (hand !== 'none' && hand !== moveHand) continue;
        const mag = Math.hypot(intent.moveX, intent.moveZ);
        if (mag < STICK_LIVE) continue;
        // Head-relative, turned exactly the way `player/controller.ts` turns the
        // keyboard's. Hand-relative steering would need a body facing that is
        // not the look direction, which nothing in this codebase can represent.
        const sin = Math.sin(player.yaw);
        const cos = Math.cos(player.yaw);
        const wx = sin * intent.moveZ + cos * intent.moveX;
        const wz = -cos * intent.moveZ + sin * intent.moveX;
        const len = Math.hypot(wx, wz);
        if (len < 1e-4) continue;
        req.dirX = wx / len;
        req.dirZ = wz / len;
        req.byRay = false;
        req.reach = 0;
        req.backward = intent.moveZ < 0;
        req.secondary = intent.secondary;
        req.ended = intent.actionEnded;
        req.ray = intent.ray;
        req.live = true;
        return true;
      }
    }

    for (const intent of set.all) {
      if (!intent.acting || intent.heldFor < HOLD_ARM_TIME) continue;
      const hand = handOf(intent);
      if (hand !== 'none' && hand !== moveHand) continue;
      const ray = intent.ray;
      if (!ray) continue;
      // A controller's ray never drives a dash; see above. It does aim an arc.
      if (style === 'dash' && intent.source === 'controller') continue;
      if (this.fromRay(ray, player, req)) {
        req.secondary = intent.secondary;
        req.ended = intent.actionEnded;
        req.ray = ray;
        req.live = true;
        return true;
      }
    }
    return false;
  }

  /**
   * A ray — a controller's, or the gaze ray a pinch briefly exposes — turned
   * into a bearing and, when it lands on the floor near the player, a reach.
   *
   * The reach is what makes gaze-dash also work as point-and-go over short
   * distances: look at a spot two metres away and the dash stops there rather
   * than overshooting it, and once the player is standing on it the reach falls
   * under `MIN_HOP` and the dash ends by itself.
   */
  private fromRay(
    ray: { origin: THREE.Vector3; direction: THREE.Vector3 },
    player: PlayerState,
    req: Request
  ): boolean {
    const d = ray.direction;
    const o = ray.origin;
    const flat = Math.hypot(d.x, d.z);
    if (flat < RAY_FLAT) return false;

    req.byRay = true;
    req.reach = 0;
    req.dirX = d.x / flat;
    req.dirZ = d.z / flat;

    if (d.y < -1e-3 && o.y > 0) {
      // Straight to the floor plane. Every floor in this game is at y = 0, so
      // this is exact rather than an approximation of a raycast.
      const t = -o.y / d.y;
      const dx = o.x + d.x * t - player.pos.x;
      const dz = o.z + d.z * t - player.pos.z;
      const len = Math.hypot(dx, dz);
      if (len < MIN_HOP) return false;
      req.dirX = dx / len;
      req.dirZ = dz / len;
      req.reach = len;
    }

    // Backpedalling is slower in the flat game and has to be here too.
    req.backward = req.dirX * Math.sin(player.yaw) + req.dirZ * -Math.cos(player.yaw) < 0;
    return true;
  }

  /* ---------------------------------------------------------------- *
   * The two styles
   * ---------------------------------------------------------------- */

  /** Hold-to-dash: one short hop per billed interval, for as long as it is held. */
  private planDash(player: PlayerState, level: GeneratedLevel): void {
    const req = this.req;
    let want = player.crouching ? HOP_CROUCH : this.sprinting ? HOP_SPRINT : HOP_WALK;
    if (req.reach > 0) want = Math.min(want, req.reach);

    const plan = planHop(
      level,
      player.pos.x,
      player.pos.z,
      req.dirX,
      req.dirZ,
      want,
      TUNING.player.radius,
      this.plan
    );
    if (plan.distance < MIN_HOP) return;
    this.beginCharge(plan.x, plan.z, plan.distance, req.backward, player, level);
  }

  /**
   * Point-and-release, for the longer jumps a dash is not for.
   *
   * The parabola is drawn from the ray, but the landing is *swept from the
   * player's own feet*, so an arc cannot cross anything a walk could not. "I can
   * see it, so I can get there" is precisely how a teleport ends up standing
   * inside a play structure, and half-walls are explicitly not something to
   * blink over.
   */
  private updateArc(live: boolean, player: PlayerState, level: GeneratedLevel, usable: boolean): void {
    if (!live) {
      // The frame the aim stops being live is the release, which is the commit.
      // Reading `actionEnded` off the intent would not do: the producer may
      // clear the hold on the same frame it raises the flag, in which case the
      // hand is no longer the driver and the flag is never seen.
      // An arc is a deliberate jump to a place rather than a step, so it is
      // never billed at the backpedal rate however far behind the player it is.
      if (this.aiming && usable && this.aimValid && !this.charging) {
        this.beginCharge(this.aimX, this.aimZ, this.aimDistance, false, player, level);
      }
      this.cancelAim();
      return;
    }

    const ray = this.req.ray;
    if (!ray) return;
    const o = ray.origin;
    const d = ray.direction;
    // Ballistic solve against the floor plane; o.y is above it in every real
    // pose, so the positive root always exists.
    const vy = d.y * ARC_SPEED;
    const t = (vy + Math.sqrt(vy * vy + 2 * ARC_GRAVITY * Math.max(o.y, 0.01))) / ARC_GRAVITY;

    let dirX = o.x + d.x * ARC_SPEED * t - player.pos.x;
    let dirZ = o.z + d.z * ARC_SPEED * t - player.pos.z;
    const raw = Math.hypot(dirX, dirZ);
    if (raw < 1e-4) return;
    dirX /= raw;
    dirZ /= raw;

    const plan = planHop(
      level,
      player.pos.x,
      player.pos.z,
      dirX,
      dirZ,
      Math.min(raw, ARC_MAX),
      TUNING.player.radius,
      this.plan
    );

    this.aiming = true;
    this.aimX = plan.x;
    this.aimZ = plan.z;
    this.aimDistance = plan.distance;
    // In debt to the speed budget, or with nowhere to land: drawn and refused,
    // because an arc that silently did nothing reads as a broken headset.
    this.aimValid = plan.distance >= MIN_HOP && this.cooldown <= 0;
    this.drawArc(o, d, t, plan.distance / raw);

    // A producer that raises `actionEnded` while the hand is still reporting the
    // press commits here; one that drops the hand on the same frame commits
    // through the latch above. Whichever arrives first clears the aim, so the
    // two can never both fire.
    if (this.req.ended && this.aimValid && !this.charging) {
      this.beginCharge(plan.x, plan.z, plan.distance, false, player, level);
      this.cancelAim();
    }
  }

  /** Lay the drawn parabola down, truncated where the sweep actually stopped. */
  private drawArc(
    o: THREE.Vector3,
    d: THREE.Vector3,
    tHit: number,
    frac: number
  ): void {
    const view = this.view;
    if (!view) return;
    // Horizontal travel is linear in t, so truncating at the fraction the sweep
    // stopped at lands the curve's tip exactly on the ring.
    const end = tHit * clamp01(frac);
    const points = this.aimPoints;
    for (let i = 0; i < ARC_POINTS; i++) {
      const t = (end * i) / (ARC_POINTS - 1);
      points[i * 3] = o.x + d.x * ARC_SPEED * t;
      points[i * 3 + 1] = Math.max(0.02, o.y + d.y * ARC_SPEED * t - 0.5 * ARC_GRAVITY * t * t);
      points[i * 3 + 2] = o.z + d.z * ARC_SPEED * t;
    }
    view.show(points, this.aimX, this.aimZ, this.aimValid);
  }

  private cancelAim(): void {
    if (!this.aiming) return;
    this.aiming = false;
    this.aimValid = false;
    this.view?.hide();
  }

  /* ---------------------------------------------------------------- *
   * The hop itself
   * ---------------------------------------------------------------- */

  /**
   * Start the fade a hop lands at the bottom of, and bill it.
   *
   * Billed here rather than at the commit, which is the difference between a
   * cadence that comes out at the flat game's speed and one that comes out
   * about eight per cent under it: the fade is part of the hop, so it has to be
   * spent *inside* the interval the hop paid for rather than added on top of it.
   * Measured before this was moved, a held walk-dash made 2.40 m/s against a
   * `walkSpeed` of 2.60, and a sprint 4.63 against 5.00.
   */
  private beginCharge(
    x: number,
    z: number,
    distance: number,
    backward: boolean,
    player: PlayerState,
    level: GeneratedLevel
  ): void {
    this.charging = true;
    this.chargeT = 0;
    this.chargeFor = FADE_OUT_BASE + FADE_OUT_PER_METRE * distance;
    const k = clamp01((distance - FADE_NEAR_DIST) / (FADE_FAR_DIST - FADE_NEAR_DIST));
    this.chargeDepth = FADE_NEAR_DEPTH + (FADE_FAR_DEPTH - FADE_NEAR_DEPTH) * k;
    this.pendX = x;
    this.pendZ = z;
    this.pendDistance = distance;
    this.pendBackward = backward;
    this.pendWater = inWaterAt(level, x, z);
    this.pendCost = distance / Math.max(this.stanceSpeed(player, backward, this.pendWater), 0.05);
    this.cooldown += this.pendCost;
  }

  /**
   * Run the fade, and commit when it reaches the bottom.
   *
   * The destination is a *place*, so the delta is measured against wherever the
   * body is at the moment of the commit rather than against where it was when
   * the hop was planned — the player's own legs are still moving the body under
   * `settleBody` while a hop charges, and a stored delta would double-count
   * them. A charge is under a seventh of a second, so nothing has gone far.
   */
  private advanceCharge(dt: number, player: PlayerState, out: XRHop): void {
    out.fade = this.chargeDepth;
    this.chargeT += dt;
    if (this.chargeT < this.chargeFor) return;
    this.charging = false;

    out.dx = this.pendX - player.pos.x;
    out.dz = this.pendZ - player.pos.z;
    out.distance = this.pendDistance;
    out.moved = true;

    // Only bookkeeping: the bill was taken when the hop was decided.
    this.lastWater = this.pendWater;
    this.lastDistance = this.pendDistance;
    this.lastCost = this.pendCost;
    this.sinceHop = 0;
  }

  /**
   * The speed the flat game would have moved this player at.
   *
   * Every scale `player/controller.ts` applies, applied here: stance, injury,
   * water and the backpedal penalty. Nothing else decides how fast a teleporting
   * player crosses a floor.
   */
  private stanceSpeed(player: PlayerState, backward: boolean, water: boolean): number {
    const P = TUNING.player;
    const base = player.crouching ? P.crouchSpeed : this.sprinting ? P.sprintSpeed : P.walkSpeed;
    return (
      base *
      Math.max(0, player.speedScale) *
      (water ? P.waterSpeedScale : 1) *
      (backward ? P.backwardScale : 1)
    );
  }

  /**
   * Publish the speed and the noise.
   *
   * The speed estimate is deliberately not a moving average. The billed cost of
   * the last hop *is* the time that hop was supposed to take, so
   * `distance / cost` is the stance speed by construction while hops keep
   * landing, and it stretches on its own the moment they stop — which is what a
   * player grinding into a corner should read as. Once the last hop's billed
   * span has elapsed with nothing new, it drops to zero outright, so letting go
   * goes quiet on the same schedule as taking your hand off the keyboard.
   */
  private publish(dt: number, dashing: boolean, player: PlayerState, out: XRHop): void {
    const P = TUNING.player;
    const covering = this.lastDistance > 0 && (dashing || this.sinceHop < this.lastCost);
    const speed = covering ? this.lastDistance / Math.max(this.sinceHop, this.lastCost, 1e-3) : 0;

    // A transcription of `player/footsteps.ts`: same references, same effort
    // curve, same water bonus, same damping. A dash has to cost the player
    // exactly what the equivalent walk or run would.
    const reference = player.crouching ? P.crouchSpeed : this.sprinting ? P.sprintSpeed : P.walkSpeed;
    const effort = clamp01(speed / Math.max(reference, 0.1));
    const base = player.crouching ? P.noiseCrouch : this.sprinting ? P.noiseSprint : P.noiseWalk;
    const target = effort < 0.05 ? 0 : (base + (this.lastWater ? P.noiseWaterBonus : 0)) * effort;
    this.noise = damp(this.noise, target, NOISE_RATE, dt);

    out.speed = speed;
    out.noise = this.noise;
    out.travelling = covering;
    out.sprinting = this.sprinting;
  }
}
